import { trim } from "lodash";
import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Input from "antd/lib/input";
import { useTheme } from "@/components/ThemeProvider";

import "./EditInPlace.less";

function EditInPlaceContent({
  ignoreBlanks,
  isEditable,
  placeholder,
  value,
  onDone,
  onStopEditing,
  multiline,
  editorProps,
  defaultEditing,
  className,
}) {
  const { isDarkMode } = useTheme();
  const [editing, setEditing] = React.useState(defaultEditing);

  React.useEffect(() => {
    if (!editing) {
      onStopEditing();
    }
  }, [editing, onStopEditing]);

  const startEditing = () => {
    if (isEditable) {
      setEditing(true);
    }
  };

  const stopEditing = currentValue => {
    const newValue = trim(currentValue);
    const ignorableBlank = ignoreBlanks && newValue === "";
    if (!ignorableBlank && newValue !== value) {
      onDone(newValue);
    }
    setEditing(false);
  };

  const handleKeyDown = event => {
    if (event.keyCode === 13 && !event.shiftKey) {
      event.preventDefault();
      stopEditing(event.target.value);
    } else if (event.keyCode === 27) {
      setEditing(false);
    }
  };

  const renderNormal = () =>
    value ? (
      <span
        role="presentation"
        onFocus={startEditing}
        onClick={startEditing}
        className={classNames({ editable: isEditable, "dark-mode": isDarkMode })}>
        {value}
      </span>
    ) : (
      <a
        className={classNames("clickable", { "dark-mode": isDarkMode })}
        onClick={startEditing}>
        {placeholder}
      </a>
    );

  const renderEdit = () => {
    const InputComponent = multiline ? Input.TextArea : Input;
    return (
      <InputComponent
        defaultValue={value}
        aria-label="Editing"
        onBlur={e => stopEditing(e.target.value)}
        onKeyDown={handleKeyDown}
        autoFocus
        className={classNames({ "dark-mode": isDarkMode })}
        {...editorProps}
      />
    );
  };

  return (
    <span
      className={classNames("edit-in-place", { active: editing, "dark-mode": isDarkMode }, className)}>
      {editing ? renderEdit() : renderNormal()}
    </span>
  );
}

EditInPlaceContent.propTypes = {
  ignoreBlanks: PropTypes.bool,
  isEditable: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onDone: PropTypes.func.isRequired,
  onStopEditing: PropTypes.func,
  multiline: PropTypes.bool,
  editorProps: PropTypes.object,
  defaultEditing: PropTypes.bool,
  className: PropTypes.string,
};

EditInPlaceContent.defaultProps = {
  ignoreBlanks: false,
  isEditable: true,
  placeholder: "",
  value: "",
  onStopEditing: () => {},
  multiline: false,
  editorProps: {},
  defaultEditing: false,
  className: "",
};

function EditInPlace(props) {
  return <EditInPlaceContent {...props} />;
}

EditInPlace.propTypes = EditInPlaceContent.propTypes;
EditInPlace.defaultProps = EditInPlaceContent.defaultProps;

export default EditInPlace;
